import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  updateJobPositionSchema,
  type UpdateJobPositionFormData,
} from '../schemas/jobPosition.schemas';
import { JobPositionFormFields } from './JobPositionFormFields';
import type { JobPosition } from '../types/jobPosition.types';
import { useEditJobPositionHandlers } from './EditJobPositionDialog.hooks';

interface EditJobPositionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobPosition: JobPosition | null;
}

export function EditJobPositionDialog({
  open,
  onOpenChange,
  jobPosition,
}: EditJobPositionDialogProps): JSX.Element {
  const { isLoading, handleUpdate } = useEditJobPositionHandlers(jobPosition, onOpenChange);
  const form = useForm<UpdateJobPositionFormData>({
    resolver: zodResolver(updateJobPositionSchema) as never,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = form;

  useEffect(() => {
    if (jobPosition) {
      const { startDate, ...rest } = jobPosition;
      reset({ ...rest, startDate: startDate?.substring(0, 10) });
    }
  }, [jobPosition, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-[700px]"
        data-row-click-ignore
      >
        <DialogHeader>
          <DialogTitle>Edit Job Position</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleUpdate)} className="space-y-4">
          <JobPositionFormFields
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          <FormFooter isLoading={isLoading} onCancel={() => onOpenChange(false)} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormFooter({
  isLoading,
  onCancel,
}: {
  isLoading: boolean;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Updating...' : 'Update'}
      </Button>
    </DialogFooter>
  );
}
